import React from "react";
import { Autocomplete, Box, InputAdornment, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { fieldSx } from "./formStyles";

const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, "")); return Number.isFinite(n) ? n : 0; };
const bs = (n) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fechaCorta = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || "")); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };

// Bloque con título (barra de encabezado + cuerpo), el mismo estilo de las secciones del resto de Egresos
const Seccion = ({ titulo, nota, children }) => (
  <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper", overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 1.25, py: 0.4, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", borderLeft: "3px solid", borderLeftColor: "primary.main" }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", color: "primary.main" }}>{titulo}</Typography>
      {nota && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{nota}</Typography>}
    </Box>
    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

// Franja de resumen del movimiento: crédito en verde y débito en rojo, con el monto total y lo principal del banco.
export function ResumenMovimiento({ form }) {
  const esDebito = form.tipo === "debito";
  const monto = num(form.monto);
  const total = esDebito ? monto + num(form.itf) : monto;
  const color = esDebito ? "#c62828" : "#2e7d32";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.5, py: 0.9, border: 1, borderColor: color, bgcolor: alpha(color, 0.07) }}>
      <Box sx={{ width: 34, height: 34, borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", bgcolor: color }}>
        {esDebito ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase", color }}>{esDebito ? "Débito · sale dinero" : "Crédito · entra dinero"}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>Bs {bs(total)}</Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
          {[form.banco, fechaCorta(form.fecha), form.cod_transaccion && `Cód. ${form.cod_transaccion}`].filter(Boolean).join(" · ") || "Completa los datos del movimiento"}
          {esDebito && num(form.itf) > 0 ? ` · incluye ITF ${bs(num(form.itf))}` : ""}
        </Typography>
      </Box>
    </Box>
  );
}

// Campos de un movimiento del extracto, agrupados por secciones. `bloqueado` deja solo editables la referencia, la descripción y el dato adicional.
export default function MovimientoFormulario({ form, setForm, bancos = [], bloqueado = false }) {
  const esDebito = form.tipo === "debito";
  const cambiar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <ResumenMovimiento form={form} />

      <Seccion titulo="Tipo, monto y fecha">
        <Box sx={{ display: "grid", gap: 1.25 }}>
          <ToggleButtonGroup
            exclusive fullWidth size="small" value={form.tipo} disabled={bloqueado} onChange={(_, v) => v && setForm((f) => ({ ...f, tipo: v }))}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, borderRadius: 0, py: 0.4 } }}
          >
            <ToggleButton value="credito" color="success">Crédito (entra dinero)</ToggleButton>
            <ToggleButton value="debito" color="error">Débito (sale dinero)</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: esDebito ? "1fr 1fr 1fr" : "1fr 1fr" }, gap: 1 }}>
            <TextField size="small" type="date" label="Fecha" required disabled={bloqueado} value={form.fecha} onChange={cambiar("fecha")} slotProps={{ inputLabel: { shrink: true } }} sx={fieldSx} />
            <TextField
              size="small" label="Monto" required disabled={bloqueado} value={form.monto} onChange={cambiar("monto")} placeholder="0.00" sx={fieldSx}
              slotProps={{ input: { startAdornment: <InputAdornment position="start">Bs</InputAdornment> }, htmlInput: { inputMode: "decimal" } }}
            />
            {esDebito && (
              <TextField size="small" label="ITF" disabled={bloqueado} value={form.itf} onChange={cambiar("itf")} placeholder="0.00" sx={fieldSx} helperText="Se suma al débito" slotProps={{ htmlInput: { inputMode: "decimal" } }} />
            )}
          </Box>
        </Box>
      </Seccion>

      <Seccion titulo="Banco" nota="como figura en el extracto">
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1.4fr" }, gap: 1 }}>
          <Autocomplete
            freeSolo size="small" disabled={bloqueado} options={bancos} value={form.banco} inputValue={form.banco}
            onInputChange={(_, v) => setForm((f) => ({ ...f, banco: v }))}
            renderInput={(params) => <TextField {...params} label="Banco" required sx={fieldSx} slotProps={{ htmlInput: { ...params.inputProps, maxLength: 25 } }} />}
          />
          <TextField size="small" label="Código de transacción" disabled={bloqueado} value={form.cod_transaccion} onChange={cambiar("cod_transaccion")} sx={fieldSx} helperText="El que figura en el extracto del banco (si lo tiene)" slotProps={{ htmlInput: { maxLength: 50 } }} />
        </Box>
      </Seccion>

      <Seccion titulo="Detalle" nota="texto libre, siempre se puede corregir">
        <Box sx={{ display: "grid", gap: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
            <TextField size="small" label="Referencia" value={form.referencia} onChange={cambiar("referencia")} sx={fieldSx} slotProps={{ htmlInput: { maxLength: 150 } }} />
            <TextField size="small" label="Descripción" value={form.descripcion} onChange={cambiar("descripcion")} sx={fieldSx} slotProps={{ htmlInput: { maxLength: 150 } }} />
          </Box>
          <TextField size="small" label="Dato adicional" multiline minRows={2} value={form.adicional} onChange={cambiar("adicional")} sx={fieldSx} slotProps={{ htmlInput: { maxLength: 255 } }} />
        </Box>
      </Seccion>
    </Box>
  );
}
